import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab, Tooltip } from '@mui/material';
import * as React from 'react';
import { Container, CurrentContainerStatus } from 'types/Watchlist';
import { useSegment } from 'common/hooks/segment';
import {
  checkForPermission,
  Permission,
  PermissionRequirement,
  usePermissions,
} from 'utils/permissions';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FeatureFlags } from 'utils/permissions/feature-flags';
import { shouldShowPerDiem } from 'utils/container-transforms';
import { Availability } from './availability';
import { PerDiem } from './per-diem';
import { Summary } from './summary';
import { History } from './history';

type SectionKey = 'summary' | 'availability' | 'perdiem' | 'history';

interface Props {
  container: Container;
  children: React.ReactNode;
}

interface Section {
  key: SectionKey;
  label: React.ReactNode;
  component: JSX.Element;
  permissions?: PermissionRequirement;
  disabled: boolean;
}

const determineSection: (
  status: CurrentContainerStatus,
  permissions: Permission[],
  flags: FeatureFlags
) => SectionKey = (status) => {
  switch (status) {
    case CurrentContainerStatus.GatedOut:
    case CurrentContainerStatus.Terminated:
      return 'perdiem';
    case CurrentContainerStatus.AtTerminal:
      return 'availability';
    default:
      return 'summary';
  }
};

export const Sections: React.FC<Props> = (props) => {
  const { container, children } = props;
  const permissions = usePermissions();
  const { track } = useSegment();
  const flags = useFlags();

  const showPerDiem = shouldShowPerDiem(container);

  const [value, setValue] = React.useState<SectionKey>(
    determineSection(container.currentStatus, permissions, flags)
  );

  React.useEffect(() => {
    setValue(determineSection(container.currentStatus, permissions, flags));
  }, [container.currentStatus, permissions, flags]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const sections: Section[] = React.useMemo(() => {
    const items: Section[] = [
      {
        key: 'summary',
        label: <span>Summary</span>,
        component: <Summary container={container} />,
        disabled: false,
      },
      {
        key: 'availability',
        label: <span>Container availability</span>,
        component: <Availability container={container} />,
        disabled: false,
      },
      {
        key: 'perdiem',
        label: showPerDiem ? (
          <span>Per diem disputes</span>
        ) : (
          <Tooltip title="The Smart Per Diem report™ is unavailable for this container yet because it has not been gated out">
            <span>Per diem disputes</span>
          </Tooltip>
        ),
        component: <PerDiem container={container} />,
        disabled: !showPerDiem,
      },
      {
        key: 'history',
        label: <span>History</span>,
        component: <History container={container} />,
        permissions: [Permission.ShipmentWatchListTabHistory],
        disabled: false,
      },
    ];

    return items.filter((item) => checkForPermission(item.permissions, permissions));
  }, [container, permissions, showPerDiem]);

  return (
    <TabContext value={value}>
      <div className="sticky top-0 bg-white z-50">
        {children}
        <TabList
          onChange={(_, newValue) => {
            track(
              'Container side drawer tab clicked',
              { tab: newValue },
              { includeCompany: true, includeUser: true }
            );
            setValue(newValue);
          }}
          className="shadow-sm"
        >
          {sections.map((section) => (
            <Tab
              key={section.key}
              disabled={section.disabled}
              label={section.label}
              value={section.key}
              className="capitalize flex-grow pointer-events-auto"
            />
          ))}
        </TabList>
      </div>
      {sections.map((section) => (
        <TabPanel value={section.key} key={section.key} className="p-0 flex-grow">
          {section.key === 'perdiem' && !showPerDiem ? null : section.component}
        </TabPanel>
      ))}
    </TabContext>
  );
};
Sections.displayName = 'Shipments.DetailDrawer.Sections';
